import { BehaviorSubject, Observable, Subject, interval, of } from 'rxjs';
import {
  tap,
  delay,
  filter,
  startWith,
  takeWhile,
  takeUntil,
  exhaustMap,
  distinctUntilChanged,
} from 'rxjs/operators';
import * as screenfull from 'screenfull';

import {
  Input,
  OnInit,
  Output,
  Optional,
  SkipSelf,
  OnDestroy,
  Component,
  ViewChild,
  forwardRef,
  ElementRef,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { snFadeIn, snFadeOut } from '@lib/common/animations';
import { SnAspectRatio } from '@lib/common/enums';
import { genericRetryStrategy } from '@lib/common/helpers';
import { SnPlayerGroupDirective, SnPlayerState, ISnPlayer } from '@lib/player/common';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';
import { SnPlayerService } from 'libs/player/common/src/lib/services/player.service';

@Component({
  selector: 'sn-image-player',
  templateUrl: './image-player.component.html',
  styleUrls: ['./image-player.component.scss'],
  animations: [snFadeIn, snFadeOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: 'SN_PLAYER',
      useExisting: forwardRef(() => SnImagePlayerComponent),
    },
  ],
  host: {
    class: 'sn-image-player',
  },
})
export class SnImagePlayerComponent implements OnInit, OnDestroy, ISnPlayer {
  @Input()
  public src: string;

  @Input()
  public caption: string;

  @Input()
  public elevation: number;

  @Input()
  public refreshTime: number;

  @Input()
  public autoplay = false;

  @Input()
  public controls = true;

  @Input()
  public width: string | number;

  @Input()
  public height: string | number;

  @Input()
  public aspectRatio: number | SnAspectRatio;

  @Output()
  public readonly init = new EventEmitter<ISnPlayer>();

  public readonly live = true;

  public hasOverlay = false;

  @ViewChild('player', { read: ElementRef, static: true })
  private readonly _playerElementRef: ElementRef;

  private _frameSrc: string;
  private _isLoading = false;

  private _currentTime = 0;
  private _totalTime = 0;

  private _fullscreen = false;

  private readonly _buffer = new Map<number, string>();

  private readonly _screenfull = screenfull;

  private readonly _clear$ = new Subject();

  private readonly _state$ = new BehaviorSubject<SnPlayerState>(SnPlayerState.Pending);
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectionRef: ChangeDetectorRef,
    @Optional() @SkipSelf() private readonly _group: SnPlayerGroupDirective,
    private readonly _matDialog: MatDialog,
    private readonly _playerService: SnPlayerService,
  ) {}

  get frameSrc(): string {
    return this._frameSrc;
  }

  get currentTime(): number {
    return this._currentTime;
  }

  get bufferTime(): number {
    return this._totalTime;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get state$(): Observable<SnPlayerState> {
    return this._state$.asObservable();
  }

  get play$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Playing));
  }

  get pause$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Pause));
  }

  get end$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Ended));
  }

  get ended$(): Observable<void> {
    return new Subject<void>();
  }

  get live$(): Observable<boolean> {
    return new BehaviorSubject<boolean>(true);
  }

  private get _endedPlayback(): boolean {
    return this._currentTime === this._totalTime;
  }

  public ngOnInit(): void {
    if (this.autoplay) {
      this._startLoad();
    }

    if (this._group) {
      this._group.items.add(this);
    }

    this.init.emit(this);

    this._initSubscriptions();
  }

  public ngOnDestroy(): void {
    this._state$.next(SnPlayerState.Ended);
    this._clear$.next();
    this._clear$.complete();
    this.init.complete();

    if (this._group) {
      this._group.items.delete(this);
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  public play(): void {
    this._state$.next(SnPlayerState.Playing);
  }

  public pause(): void {
    this._state$.next(SnPlayerState.Pause);
  }

  // I don't think this function is used and probably can be removed?
  public changeFullscreen(): void {
    if (this._screenfull.isEnabled) {
      this._screenfull.toggle(this._playerElementRef.nativeElement);
      this._fullscreen = !this._fullscreen;
    } else {
      if (this._fullscreen) {
        this._playerElementRef.nativeElement.classList.remove('fullscreen');
      } else {
        this._playerElementRef.nativeElement.classList.add('fullscreen');
      }
      this._fullscreen = !this._fullscreen;
    }
  }

  public exitedFullscreen(): void {
    this._playerService.fullscreenClose();
  }

  public clip(): void {
    if (this._fullscreen) {
      this.changeFullscreen();
    }
    const image = this._playerElementRef.nativeElement.querySelector('img');
    this.hasOverlay = true;
    const dialog = this._matDialog.open(SnShareImageDialogComponent, {
      data: { url: image.src },
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.hasOverlay = false;
      });
  }

  private _initSubscriptions(): void {
    this.state$
      .pipe(distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe((state: SnPlayerState) => {
        switch (state) {
          case SnPlayerState.Playing:
            this._onPlay();
            break;
          case SnPlayerState.Pause:
            this._onPause();
            break;
          case SnPlayerState.Ended:
            this._onEnd();
            break;
        }
      });
  }

  private _onPlay(): void {
    if (this._state$.value === SnPlayerState.Ended && this._endedPlayback) {
      this._replay();
    } else if (this._state$.value === SnPlayerState.Ended) {
      this._startPlay();
    } else if (!this._frameSrc) {
      this._startLoad();
    }
  }

  private _onPause(): void {
    this._clear$.next();
  }

  private _onEnd(): void {
    this._frameSrc = null;
    this._clear$.next();
  }

  private _startPlay(): void {
    this.pause();

    interval(this.refreshTime * 1000)
      .pipe(
        startWith(0),
        takeWhile(() => !this._endedPlayback),
        takeUntil(this._clear$),
      )
      .subscribe({
        next: () => this._updatePlaybackPosition(),
        complete: () => this.pause(),
      });
  }

  private _replay(): void {
    this._totalTime = 0;
    this._currentTime = 0;
    this._buffer.clear();
    this._frameSrc = null;
    this._startLoad();
  }

  private _startLoad(): void {
    this._isLoading = this.src ? true : false;
    if (!this._isLoading) {
      return;
    }
    const load$ = this.refreshTime ? interval(this.refreshTime * 1000) : of(null);
    load$
      .pipe(
        startWith(0),
        exhaustMap(() => this._loadNextFrame()),
        tap({
          next: () => {
            this._frameSrc = null;
            this._isLoading = true;
            this._changeDetectionRef.detectChanges();
          },
          error: () => {
            if (this._endedPlayback) {
              this._isLoading = true;
              this._changeDetectionRef.detectChanges();
            } else {
              this._updatePlaybackPosition();
            }
          },
        }),
        genericRetryStrategy({
          initialInterval: 1000,
          maxRetries: 3,
        }),
        delay(300),
        takeUntil(this._clear$),
      )
      .subscribe({
        next: (source) => {
          this._frameSrc = source;
          this._isLoading = false;

          this.play();

          this._totalTime += this.refreshTime;
          this._buffer.set(this._totalTime, source);

          this._updatePlaybackPosition();
        },
        error: () => {
          this._isLoading = false;
          this.pause();
          this._state$.next(SnPlayerState.Ended);
        },
      });
  }

  private _updatePlaybackPosition(): void {
    if (!(this._state$.value === SnPlayerState.Pause)) {
      this._currentTime += this.refreshTime;
      this._changeDetectionRef.detectChanges();
    }
  }

  private _loadNextFrame(): Observable<string> {
    return new Observable((observer) => {
      const image = new Image();
      const join = this.src.indexOf('?') > -1 ? '&' : '?';
      image.src = this.refreshTime ? `${this.src}${join}ts=${Date.now()}` : this.src;
      image.onload = () => {
        observer.next(image.src);
        observer.complete();
      };

      image.onerror = () => {
        observer.error('No playable source found');
      };
    });
  }
}
